import { Button } from '@/components/ui/button';
import { MobileCarousel } from '@/components/MobileCarousel';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Copyright, FileCheck, Shield, Award, Star, Clock } from 'lucide-react';

export default function CopyrightRegistrationPage({ onCTAClick }) {
  return (
    <div>
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1764601841468-e85fd94de01e?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-accent mb-4">Copyright Registration Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-primary mb-6 leading-tight">
              Professional Copyright Registration for Creative Works
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Protect your original creative works including books, music, software, artwork, photographs, and videos with official US copyright registration. Our experts handle the complete filing process with the U.S. Copyright Office.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" onClick={onCTAClick}>
              Register My Copyright
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                Complete US Copyright Registration Protection
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Copyright registration provides federal protection for your original creative works. When you register a copyright with the U.S. Copyright Office, you establish a public record of your copyright claim and gain important legal advantages, including the ability to file copyright infringement lawsuits and claim statutory damages and attorney's fees.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our copyright registration services cover all types of copyrightable works: literary works (books, articles, blog posts), musical works and sound recordings, dramatic works (plays, screenplays), choreographic works, pictorial and graphic works (photographs, artwork, graphics), audiovisual works (videos, films), and software code. We handle the complete filing process, ensuring your creative assets receive proper legal protection.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img src="https://images.unsplash.com/photo-1593444285553-28163240e3f1" alt="Copyright documents" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Why Register Your Copyright?
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Legal Evidence of Ownership', desc: 'Copyright registration establishes a public record of your copyright claim and serves as prima facie evidence of ownership in court.' },
              { icon: Award, title: 'Infringement Lawsuits', desc: 'Registration is required before you can file a copyright infringement lawsuit in federal court to enforce your rights.' },
              { icon: FileCheck, title: 'Statutory Damages', desc: 'Registered copyrights are eligible for statutory damages and attorney fees in infringement cases, often much higher than actual damages.' },
              { icon: Copyright, title: 'Customs Protection', desc: 'Registration allows you to record your copyright with U.S. Customs to prevent importation of infringing copies.' },
              { icon: Clock, title: 'Fast Processing', desc: 'Copyright registration typically takes 3-6 months, much faster than trademark registration.' },
              { icon: Check, title: 'Lifetime Protection', desc: 'Copyright protection lasts for the life of the author plus 70 years, providing long-term asset protection.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <item.icon className="h-12 w-12 text-accent mb-6" />
                <h3 className="text-xl font-bold text-primary mb-4">{item.title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              What Our Copyright Services Include
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Work Analysis & Review', desc: 'We review your creative work to determine copyrightability, identify the proper registration category, and ensure compliance with Copyright Office requirements.' },
              { title: 'Application Preparation', desc: 'Our experts prepare your copyright application with accurate work descriptions, proper classification, authorship details, and all required documentation.' },
              { title: 'Electronic Filing', desc: 'We handle the complete online filing process with the U.S. Copyright Office, including payment of government fees and submission of deposit copies.' },
              { title: 'Application Monitoring', desc: 'We track your application throughout the review process, respond to any Copyright Office inquiries, and provide regular status updates.' },
              { title: 'Registration Certificate', desc: 'Once approved, we deliver your official copyright registration certificate, which serves as legal proof of your copyright ownership.' },
              { title: 'Ongoing Support', desc: 'We provide guidance on copyright notice requirements, infringement monitoring options, and renewal procedures when applicable.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                  <Check className="h-6 w-6 text-accent" />
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Simple 3-Step Copyright Registration Process
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">1</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Submit Your Work Details</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Complete our online form with information about your creative work, including title, authorship details, publication status, and work category. Upload digital copies of your work for filing.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">2</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">We File Your Copyright</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Our team prepares your copyright application with accurate classifications and descriptions, then electronically files it with the U.S. Copyright Office along with required deposit copies.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">3</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Receive Your Certificate</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  We monitor your application and respond to any Copyright Office inquiries. Once approved (typically 3-6 months), we deliver your official copyright registration certificate.
                </p>
              </div>
            </div>
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-border p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-base leading-relaxed text-muted-foreground mb-6">
                  "Excellent copyright registration service. They made the process simple and handled everything professionally. My copyright was registered quickly and I received my certificate without any issues."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                    {['AS', 'KJ', 'RT'][i-1]}
                  </div>
                  <div>
                    <div className="font-semibold text-primary">{['Alex Smith', 'Karen Johnson', 'Robert Taylor'][i-1]}</div>
                    <div className="text-sm text-muted-foreground">{['Author', 'Photographer', 'Software Developer'][i-1]}</div>
                  </div>
                </div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Copyright Registration FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'What can be copyrighted?', a: 'Copyright protects original works of authorship including literary works, musical compositions, dramatic works, choreography, pictorial/graphic/sculptural works, audiovisual works, sound recordings, and software code. Ideas, facts, and functional methods cannot be copyrighted.' },
              { q: 'How long does copyright protection last?', a: 'For works created after 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, anonymous works, or pseudonymous works, protection lasts 95 years from publication or 120 years from creation, whichever is shorter.' },
              { q: 'Do I need to register my copyright?', a: 'Copyright exists automatically upon creation of an original work, but registration provides significant legal advantages including the ability to file infringement lawsuits, eligibility for statutory damages, public record of ownership, and customs protection against infringing imports.' },
              { q: 'How much does online copyright registration cost?', a: 'U.S. Copyright Office filing fees are typically $45-$65 per work for electronic filing, depending on the type of work and filing method. Our service fees vary based on the complexity of your copyright filing and the level of service you choose.' }
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i+1}`} className="bg-white border border-border px-8">
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Register Your Copyright?
          </h2>
          <p className="text-lg leading-relaxed text-white/90 mb-8">
            Protect your creative works with professional copyright registration services. Get started today!
          </p>
          <Button size="lg" onClick={onCTAClick} className="bg-accent hover:bg-accent/90 text-primary">
            Start Your Copyright Registration
          </Button>
        </div>
      </section>
    </div>
  );
}
